import { useState, useEffect, useMemo } from 'react'
import {
  aplicacionesService,
  mensajeErrorAplicacion,
  repartirPagoFifo,
  type DeudaPendiente,
  type PagoConSaldo,
} from '@/services/aplicacionesService'
import { useRegistrarPagoConAplicaciones, movimientosKeys } from '@/hooks/useMovimientos'
import { balanceForwardKeys } from '@/hooks/useBalanceForward'
import { useQueryClient } from '@tanstack/react-query'
import { useSubirDocumento, useVincularDocumento } from '@/core/hooks/useDocumentos'
import type { Cuenta } from '@/types/database'
import type { VistaSaldoClienteConImpuestos } from '@/services/clientesService'
import { formatCurrency } from '@/lib/utils'
import { toast } from 'sonner'
import { useAuth0 } from '@auth0/auth0-react'
import { usePermissions } from '@/hooks/usePermissions'
import { useReciboPago } from '@/hooks/useReciboPago'
import { useSupabase } from '@/lib/supabase-context'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { facturasKeys, usePuedeFacturar } from '@/hooks/useFacturas'
import { facturasService, type ReciboParaFactura } from '@/services/facturasService'

interface UseRegistrarPagoOptions {
  open: boolean
  onOpenChange: (open: boolean) => void
  clienteId?: string
  clienteNombre?: string
  cuentas: Cuenta[]
  clientes: VistaSaldoClienteConImpuestos[]
  onSuccess?: () => void
}

export function useRegistrarPago({
  open,
  onOpenChange,
  clienteId: clienteIdProp,
  clienteNombre: clienteNombreProp,
  clientes,
  onSuccess,
}: UseRegistrarPagoOptions) {
  // States
  const [clienteIdSeleccionado, setClienteIdSeleccionado] = useState<string>('')
  const [deudasPendientes, setDeudasPendientes] = useState<DeudaPendiente[]>([])
  const [deudasSeleccionadas, setDeudasSeleccionadas] = useState<Set<string>>(new Set())
  const [cargandoDeudas, setCargandoDeudas] = useState(false)
  const [errorDeudas, setErrorDeudas] = useState<string | null>(null)
  const [montoPago, setMontoPago] = useState<string>('')
  const [cuentaId, setCuentaId] = useState<string>('')
  const [observaciones, setObservaciones] = useState<string>('')
  const [esPagoACuenta, setEsPagoACuenta] = useState(false)
  const [guardando, setGuardando] = useState(false)
  const [mostrarConfirmacionCerrar, setMostrarConfirmacionCerrar] = useState(false)
  const [montoFormateado, setMontoFormateado] = useState<string>('')
  const [archivoComprobante, setArchivoComprobante] = useState<File | null>(null)
  const [fechaPago, setFechaPago] = useState<string>(new Date().toISOString().split('T')[0])
  const [pagosConSaldo, setPagosConSaldo] = useState<PagoConSaldo[]>([])
  const [usarSaldoAFavor, setUsarSaldoAFavor] = useState(false)
  const [conceptoEditado, setConceptoEditado] = useState('')
  const [conceptoDirty, setConceptoDirty] = useState(false)
  // Factura electrónica del cobro (ORR-255). Apagada por defecto: una factura autorizada no se borra.
  const [emitirFactura, setEmitirFactura] = useState(false)
  const [enviarFacturaPorEmail, setEnviarFacturaPorEmail] = useState(true)

  const { user, getAccessTokenSilently } = useAuth0()
  const { organizacionId } = useOrganizacion()
  const puedeFacturar = usePuedeFacturar()
  const { supabase } = useSupabase()
  const queryClient = useQueryClient()
  const registrarPago = useRegistrarPagoConAplicaciones()
  const { puede } = usePermissions()
  // `precargar` baja el chunk del PDF al abrir el cobro, no al terminarlo: si quedó viejo tras un
  // deploy, la recarga de recuperación cae con el formulario vacío y no con el pago ya registrado
  // y el recibo sin salir (ORR-394).
  const { enviar: enviarRecibo, prepararParaFactura } = useReciboPago({ precargar: open && puede.enviarRecibo })
  const subirDocumento = useSubirDocumento()
  const vincularDocumento = useVincularDocumento()

  // Derived client info
  const clienteId = clienteIdProp || clienteIdSeleccionado
  const clienteNombre = clienteNombreProp || clientes.find(c => c.id === clienteIdSeleccionado)?.nombre || ''
  const mostrarSelectorCliente = !clienteIdProp && clientes.length > 0

  // Detect unsaved changes
  const tieneCambios = useMemo(() => {
    return montoPago !== '' || cuentaId !== '' || observaciones !== '' || deudasSeleccionadas.size > 0 || esPagoACuenta || clienteIdSeleccionado !== '' || fechaPago !== new Date().toISOString().split('T')[0] || conceptoDirty
  }, [montoPago, cuentaId, observaciones, deudasSeleccionadas.size, esPagoACuenta, clienteIdSeleccionado, fechaPago, conceptoDirty])

  // Format number with Argentine locale (1.000.000,50)
  const formatearNumero = (valor: number): string => {
    return valor.toLocaleString('es-AR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })
  }

  const handleMontoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valorIngresado = e.target.value

    if (!valorIngresado) {
      setMontoFormateado('')
      setMontoPago('')
      return
    }

    setMontoFormateado(valorIngresado)

    let valorNumerico: string
    if (valorIngresado.includes(',')) {
      valorNumerico = valorIngresado.replace(/\./g, '').replace(',', '.')
    } else {
      valorNumerico = valorIngresado.replace(/\./g, '')
    }

    setMontoPago(valorNumerico)
  }

  const handleMontoBlur = () => {
    const numero = parseFloat(montoPago) || 0
    if (numero > 0) {
      setMontoFormateado(formatearNumero(numero))
    }
  }

  const handleIntentarCerrar = (nuevoEstado: boolean) => {
    if (!nuevoEstado && tieneCambios && !guardando) {
      setMostrarConfirmacionCerrar(true)
    } else {
      onOpenChange(nuevoEstado)
    }
  }

  const confirmarCerrar = () => {
    setMostrarConfirmacionCerrar(false)
    onOpenChange(false)
  }

  // Load pending debts when dialog opens
  useEffect(() => {
    if (open && clienteId) {
      cargarDeudasPendientes()
    }
  }, [open, clienteId])

  // Reset when dialog closes
  useEffect(() => {
    if (!open) {
      setClienteIdSeleccionado('')
      setDeudasPendientes([])
      setDeudasSeleccionadas(new Set())
      setMontoPago('')
      setMontoFormateado('')
      setCuentaId('')
      setObservaciones('')
      setEsPagoACuenta(false)
      setConceptoEditado('')
      setConceptoDirty(false)
      setArchivoComprobante(null)
      setEmitirFactura(false)
      setEnviarFacturaPorEmail(true)
      setFechaPago(new Date().toISOString().split('T')[0])
      setPagosConSaldo([])
      setUsarSaldoAFavor(false)
    }
  }, [open])

  // Pre-select oldest debt (FIFO)
  useEffect(() => {
    if (deudasPendientes.length > 0 && deudasSeleccionadas.size === 0 && !esPagoACuenta) {
      const primeraDeuda = deudasPendientes[0]
      setDeudasSeleccionadas(new Set([primeraDeuda.id]))
      setMontoPago(primeraDeuda.saldo_pendiente.toString())
      setMontoFormateado(formatearNumero(primeraDeuda.saldo_pendiente))
    }
  }, [deudasPendientes, esPagoACuenta])

  const cargarDeudasPendientes = async () => {
    if (!clienteId || !supabase) return
    setCargandoDeudas(true)
    setErrorDeudas(null)
    try {
      const [deudas, pagos] = await Promise.all([
        aplicacionesService.obtenerDeudasPendientes(supabase, clienteId),
        aplicacionesService.obtenerPagosConSaldo(supabase, clienteId),
      ])
      setDeudasPendientes(deudas)
      setPagosConSaldo(pagos)
    } catch (error) {
      console.error('Error cargando deudas:', error)
      setErrorDeudas('Error al cargar las deudas pendientes')
    } finally {
      setCargandoDeudas(false)
    }
  }

  // Total of selected debts
  const totalDeudasSeleccionadas = useMemo(() => {
    return deudasPendientes
      .filter(d => deudasSeleccionadas.has(d.id))
      .reduce((sum, d) => sum + d.saldo_pendiente, 0)
  }, [deudasPendientes, deudasSeleccionadas])

  // Update amount when selected debts change
  useEffect(() => {
    if (!esPagoACuenta && deudasSeleccionadas.size > 0) {
      setMontoPago(totalDeudasSeleccionadas.toString())
      setMontoFormateado(formatearNumero(totalDeudasSeleccionadas))
    }
  }, [totalDeudasSeleccionadas, esPagoACuenta])

  // Auto-generate concept
  const conceptoGenerado = useMemo(() => {
    if (esPagoACuenta) {
      return 'Pago a cuenta'
    }

    const deudasSel = deudasPendientes.filter(d => deudasSeleccionadas.has(d.id))
    if (deudasSel.length === 0) return ''

    const servicios = new Map<string, string[]>()
    deudasSel.forEach(d => {
      const nombreServicio = d.servicio_nombre || d.concepto.split(' - ')[0] || 'Honorarios'
      const periodo = d.mes_anio || ''
      if (!servicios.has(nombreServicio)) {
        servicios.set(nombreServicio, [])
      }
      if (periodo) {
        servicios.get(nombreServicio)!.push(periodo)
      }
    })

    const partes: string[] = []
    servicios.forEach((periodos, servicio) => {
      if (periodos.length === 0) {
        partes.push(servicio)
      } else if (periodos.length === 1) {
        partes.push(`${servicio} ${periodos[0]}`)
      } else {
        const meses = periodos.map(p => p.split(' ')[0]).join('-')
        const año = periodos[0].split(' ')[1]
        partes.push(`${servicio} ${meses} ${año}`)
      }
    })

    return `Pago ${partes.join(' + ')}`
  }, [deudasPendientes, deudasSeleccionadas, esPagoACuenta])

  // Sync editable concept when auto-generated changes
  useEffect(() => {
    if (conceptoGenerado && !conceptoDirty) {
      setConceptoEditado(conceptoGenerado)
    }
  }, [conceptoGenerado, conceptoDirty])

  const toggleDeuda = (deudaId: string) => {
    const nuevaSeleccion = new Set(deudasSeleccionadas)
    if (nuevaSeleccion.has(deudaId)) {
      nuevaSeleccion.delete(deudaId)
    } else {
      nuevaSeleccion.add(deudaId)
    }
    setDeudasSeleccionadas(nuevaSeleccion)
  }

  const toggleTodas = () => {
    if (deudasSeleccionadas.size === deudasPendientes.length) {
      setDeudasSeleccionadas(new Set())
    } else {
      setDeudasSeleccionadas(new Set(deudasPendientes.map(d => d.id)))
    }
  }

  // Balance in favor
  const totalSaldoAFavor = pagosConSaldo.reduce((sum, p) => sum + p.saldo_a_favor, 0)

  // Validation
  const montoNumerico = parseFloat(montoPago) || 0
  const esPagoParcial = !esPagoACuenta && montoNumerico < totalDeudasSeleccionadas && montoNumerico > 0
  const esMontoExcedente = !esPagoACuenta && montoNumerico > totalDeudasSeleccionadas && deudasSeleccionadas.size > 0

  // El mismo reparto que se va a mandar, calculado en vivo: el aviso del formulario tiene que
  // decir a qué deudas va el excedente ANTES de guardar, no después. Una sola fuente para las
  // dos cosas — si el aviso y lo que se manda se calcularan por separado, podrían divergir.
  const repartoPrevisto = useMemo(
    () =>
      esPagoACuenta || deudasSeleccionadas.size === 0
        ? null
        : repartirPagoFifo(deudasPendientes, deudasSeleccionadas, montoNumerico),
    [deudasPendientes, deudasSeleccionadas, montoNumerico, esPagoACuenta],
  )

  const puedeGuardar = usarSaldoAFavor
    ? deudasSeleccionadas.size > 0 && totalSaldoAFavor > 0
    : montoNumerico > 0 && cuentaId && (esPagoACuenta || deudasSeleccionadas.size > 0)

  // Get predominant period from selected debts
  const periodoDelPago = useMemo(() => {
    if (esPagoACuenta) return null

    const deudasSel = deudasPendientes.filter(d => deudasSeleccionadas.has(d.id))
    if (deudasSel.length === 0) return null

    return deudasSel[0]?.mes_anio || null
  }, [deudasPendientes, deudasSeleccionadas, esPagoACuenta])

  // Save payment
  const handleGuardar = async () => {
    if (!puedeGuardar) return

    setGuardando(true)
    try {
      // Flow A: Use existing balance in favor (no new movement)
      if (usarSaldoAFavor && supabase) {
        const deudasOrdenadas = deudasPendientes
          .filter(d => deudasSeleccionadas.has(d.id))
          .sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime())

        const pagosOrdenados = [...pagosConSaldo].sort(
          (a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime()
        )

        const saldoRestanteDeuda = new Map(
          deudasOrdenadas.map(d => [d.id, d.saldo_pendiente])
        )

        // Se juntan las filas de TODOS los pagos con saldo a favor para insertarlas de
        // una (ORR-260). Antes iba una llamada por pago: si la segunda fallaba, la
        // primera ya se había aplicado y el usuario veía un error a medio camino.
        const filas: { pago_id: string; deuda_id: string; monto: number }[] = []

        for (const pago of pagosOrdenados) {
          let saldoDisponible = pago.saldo_a_favor

          for (const deuda of deudasOrdenadas) {
            if (saldoDisponible <= 0) break
            const pendiente = saldoRestanteDeuda.get(deuda.id) ?? 0
            if (pendiente <= 0) continue

            const montoAAplicar = Math.min(saldoDisponible, pendiente)
            filas.push({ pago_id: pago.id, deuda_id: deuda.id, monto: montoAAplicar })
            saldoDisponible -= montoAAplicar
            saldoRestanteDeuda.set(deuda.id, pendiente - montoAAplicar)
          }
        }

        const totalAplicado = await aplicacionesService.aplicarSaldosADeudas(
          supabase,
          filas,
          user?.email || 'sistema'
        )

        // Invalidate caches directly since Flow A doesn't create a movement via mutation
        queryClient.invalidateQueries({ queryKey: movimientosKeys.porClienteBase(clienteId) })
        queryClient.invalidateQueries({ queryKey: balanceForwardKeys.porCliente(clienteId) })
        queryClient.invalidateQueries({ queryKey: ['clientes'] })

        toast.success(`Saldo a favor aplicado a ${deudasSeleccionadas.size} deuda(s) por ${formatCurrency(totalAplicado)}`)
        onSuccess?.()
        onOpenChange(false)
        return
      }

      // Flow B: Create new payment movement
      const servicioIdDelPago = (() => {
        if (esPagoACuenta) return null
        const deudasSel = deudasPendientes.filter(d => deudasSeleccionadas.has(d.id))
        const servicioIds = [...new Set(deudasSel.map(d => d.servicio_id).filter(Boolean))]
        return servicioIds.length === 1 ? servicioIds[0] : null
      })()

      // Reparto FIFO: primero las deudas tildadas, y si sobra plata sigue sobre las abiertas
      // que quedaron sin tildar (ORR-307). Los montos van junto con el pago en una sola
      // transacción (ORR-260): la RPC los valida contra el saldo REAL y aborta entera si
      // alguno cambió, en vez de dejar el pago grabado sin aplicar.
      const aplicaciones = repartoPrevisto?.aplicaciones ?? []

      const pago = await registrarPago.mutateAsync({
        clienteId,
        monto: montoNumerico,
        fecha: fechaPago,
        concepto: conceptoEditado || conceptoGenerado,
        cuentaId,
        servicioId: servicioIdDelPago,
        mesAnio: periodoDelPago,
        observaciones: observaciones || null,
        aplicaciones,
      })

      // Upload and link receipt if attached
      if (archivoComprobante && user?.email) {
        try {
          const documento = await subirDocumento.mutateAsync({
            file: archivoComprobante,
            clienteId: clienteId,
            createdBy: user.email,
            metadatos: {
              nombre: `Comprobante ${conceptoGenerado}`,
              categoria: 'comprobantes',
            },
          })

          await vincularDocumento.mutateAsync({
            documentoId: documento.id,
            entidadTipo: 'movimiento',
            entidadId: pago.id,
            workspace: 'contable',
            rol: 'comprobante',
          })

          queryClient.invalidateQueries({ queryKey: movimientosKeys.lists() })
        } catch (docError) {
          console.error('Error subiendo comprobante:', docError)
          toast.warning('Pago registrado, pero hubo un error al adjuntar el comprobante')
        }
      }

      toast.success(
        esPagoACuenta
          ? 'Pago a cuenta registrado correctamente'
          : `Pago registrado y aplicado a ${aplicaciones.length} deuda(s)`
      )

      // Factura del cobro (ORR-255). Va fuera de la transacción: el pago ya está guardado, así que un fallo
      // acá se avisa y se reintenta desde el menú del movimiento, sin tocar el cobro. Si la factura sale por
      // mail, el recibo va en el mismo pedido y el cliente recibe los dos en un solo mail.
      let reciboPendiente = puede.enviarRecibo
      if (emitirFactura && puedeFacturar && organizacionId) {
        let recibo: ReciboParaFactura | undefined
        if (enviarFacturaPorEmail && puede.enviarRecibo) {
          const preparado = await prepararParaFactura(pago.id)
          if (preparado.estado === 'listo') recibo = preparado.recibo
          if (preparado.estado === 'sin-email') {
            reciboPendiente = false
            toast.warning(`${preparado.clienteNombre} no tiene email cargado: el recibo no se envió`)
          }
        }
        try {
          const token = await getAccessTokenSilently()
          const r = await facturasService.emitirDesdeCobro(token, organizacionId, pago.id, enviarFacturaPorEmail, recibo)
          queryClient.invalidateQueries({ queryKey: facturasKeys.all })
          if (r.reciboGuardado) reciboPendiente = false
          toast.success(
            r.reciboGuardado
              ? 'Factura pedida: el recibo y la factura le llegan al cliente en un solo mail'
              : 'Factura pedida: se emite en unos segundos y la vas a ver en el movimiento'
          )
        } catch (facturaError) {
          console.error('Error pidiendo la factura:', facturaError)
          const detalle = facturaError instanceof Error ? facturaError.message : ''
          toast.warning(`Pago registrado, pero no se pudo pedir la factura${detalle ? `: ${detalle}` : ''}. Podés emitirla desde el menú del movimiento.`)
        }
      }

      // Recibo al cliente (ORR-336), cuando no viaja con la factura. Va DESPUÉS del toast y fuera de la
      // transacción: el pago ya está commiteado, así que un fallo de mail no puede romperlo. `enviar` nunca lanza.
      if (reciboPendiente) {
        const resultado = await enviarRecibo(pago.id)
        if (resultado.estado === 'sin-email') {
          // El 15% de los cobros es de clientes sin mail. Decirlo acá es el único momento en que
          // alguien puede pedírselo; callarlo deja creer que el recibo salió.
          toast.warning(
            `${resultado.clienteNombre} no tiene email cargado: el recibo no se envió`
          )
        } else if (resultado.estado === 'error') {
          toast.warning('Pago registrado, pero no se pudo enviar el recibo. Podés reenviarlo desde el menú del movimiento.')
        } else {
          toast.success('Recibo enviado al cliente')
        }
      }

      onSuccess?.()
      onOpenChange(false)
    } catch (error) {
      console.error('Error registrando pago:', error)
      // El toast genérico anterior no dejaba ver que el problema eran los saldos viejos
      // en pantalla, y el log imprimía el objeto pelado (ORR-260).
      toast.error(mensajeErrorAplicacion(error))
      // Recargar deja el panel con los saldos reales, así el reintento no repite el
      // mismo error. El diálogo queda abierto a propósito: nada se guardó.
      await cargarDeudasPendientes()
    } finally {
      setGuardando(false)
    }
  }

  // Client selector handler
  const handleClienteChange = (value: string) => {
    setClienteIdSeleccionado(value)
    setDeudasPendientes([])
    setDeudasSeleccionadas(new Set())
    setMontoPago('')
    setMontoFormateado('')
  }

  // Pago a cuenta toggle handler
  const handlePagoACuentaChange = (checked: boolean) => {
    setEsPagoACuenta(checked)
    if (checked) {
      setDeudasSeleccionadas(new Set())
      setMontoPago('')
    }
  }

  // Usar saldo a favor handler
  const handleUsarSaldoAFavorChange = (checked: boolean) => {
    setUsarSaldoAFavor(checked)
    if (checked) {
      setMontoPago('')
      setMontoFormateado('')
      setCuentaId('')
    }
  }

  // Concepto change handler
  const handleConceptoChange = (value: string) => {
    setConceptoEditado(value)
    setConceptoDirty(true)
  }

  return {
    // Client info
    clienteId,
    clienteNombre,
    mostrarSelectorCliente,
    clienteIdSeleccionado,
    handleClienteChange,

    // Debts
    deudasPendientes,
    deudasSeleccionadas,
    cargandoDeudas,
    errorDeudas,
    totalDeudasSeleccionadas,
    toggleDeuda,
    toggleTodas,

    // Payment fields
    fechaPago,
    setFechaPago,
    montoFormateado,
    handleMontoChange,
    handleMontoBlur,
    montoNumerico,
    conceptoEditado,
    conceptoGenerado,
    handleConceptoChange,
    cuentaId,
    setCuentaId,
    observaciones,
    setObservaciones,
    archivoComprobante,
    setArchivoComprobante,

    // Factura electrónica
    puedeFacturar,
    emitirFactura,
    setEmitirFactura,
    enviarFacturaPorEmail,
    setEnviarFacturaPorEmail,

    // Toggles
    esPagoACuenta,
    handlePagoACuentaChange,
    usarSaldoAFavor,
    handleUsarSaldoAFavorChange,

    // Balance in favor
    pagosConSaldo,
    totalSaldoAFavor,

    // Validation
    esPagoParcial,
    esMontoExcedente,
    repartoPrevisto,
    puedeGuardar,

    // Actions
    guardando,
    handleGuardar,
    handleIntentarCerrar,
    confirmarCerrar,
    mostrarConfirmacionCerrar,
    setMostrarConfirmacionCerrar,
  }
}
